import { Component, signal, OnInit, inject } from '@angular/core';
import { MessageConstants } from '../../../../../constants/message.constant';
import {
  Exception,
  StorageService,
  StorageType,
  ToasterService,
  Validator,
} from '@anddone/ui-utilities';
import {
  AbstractControlOptions,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ValidationConstant } from '../../../../../constants/validation.constant';
import { Router } from '@angular/router';
import { AppSetting } from '../../../../../constants/appsetting.constant';
import { ChangePasswordUser } from '../../../../../services/change-password-user';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { PasswordValidation } from '../../../../../services/ally-common-service';

@Component({
  selector: 'app-user-change-password',
  imports: [TranslateModule, CommonModule, ReactiveFormsModule],
  templateUrl: './user-change-password.html',
  styleUrl: './user-change-password.scss',
})
export class UserChangePassword implements OnInit {
  changePasswordForm!: FormGroup;
  changePasswordformErrors: any = {};
  validator: Validator;
  // Loaders
  isLoader = signal<boolean>(false);
  loggedInUserData: any = {};
  loginData: any = {};

  // Other
  currPassFieldType = 'password';
  newPassFieldType = 'password';
  confirmPassFieldType = 'password';

  config = {
    oldPassword: {
      required: { name: ValidationConstant.changePassword.oldPassword.name },
      maxlength: {
        name: ValidationConstant.changePassword.oldPassword.name,
        max: ValidationConstant.changePassword.oldPassword.maxLength.toString(),
      },
      minlength: {
        name: ValidationConstant.changePassword.oldPassword.name,
        min: ValidationConstant.changePassword.oldPassword.minLength.toString(),
      },
      pattern: { name: ValidationConstant.changePassword.confirmPassword.name },
    },
    newPassword: {
      required: { name: ValidationConstant.changePassword.newPassword.name },
      maxlength: {
        name: ValidationConstant.changePassword.newPassword.name,
        max: ValidationConstant.changePassword.newPassword.maxLength.toString(),
      },
      minlength: {
        name: ValidationConstant.changePassword.newPassword.name,
        min: ValidationConstant.changePassword.newPassword.minLength.toString(),
      },
      pattern: { name: ValidationConstant.changePassword.newPassword.name },
      matchPassword: { name: AppSetting.pwd },
      blankPassword: { name: AppSetting.pwd },
    },
    confirmPassword: {
      required: {
        name: ValidationConstant.changePassword.confirmPassword.name,
      },
      maxlength: {
        name: ValidationConstant.changePassword.confirmPassword.name,
        max: ValidationConstant.changePassword.confirmPassword.maxLength.toString(),
      },
      minlength: {
        name: ValidationConstant.changePassword.confirmPassword.name,
        min: ValidationConstant.changePassword.confirmPassword.minLength.toString(),
      },
      pattern: { name: ValidationConstant.changePassword.confirmPassword.name },
      matchPassword: {
        name: ValidationConstant.changePassword.confirmPassword.name,
      },
      blankPassword: { name: AppSetting.pwd },
    },
  };
  private formBuilder = inject(FormBuilder);
  private toasterService = inject(ToasterService);
  private storageService = inject(StorageService);
  private userChangePasswordService = inject(ChangePasswordUser);
  private router = inject(Router);
  constructor() {
    this.validator = new Validator(this.config);
  }

  ngOnInit(): void {
    this.loginData = JSON.parse(this.storageService.get(StorageType.Session, 'auth') ?? '{}');
    const formOptions: AbstractControlOptions = {
      validators: PasswordValidation.matchPassword, // custom validator
    };
    this.changePasswordForm = this.formBuilder.group(
      {
        oldPassword: [
          '',
          [
            Validators.required,
            Validators.maxLength(ValidationConstant.changePassword.oldPassword.maxLength),
            Validators.minLength(ValidationConstant.changePassword.oldPassword.minLength),
          ],
        ],
        newPassword: [
          '',
          [
            Validators.pattern(ValidationConstant.passwordRegex),
            Validators.required,
            Validators.maxLength(ValidationConstant.changePassword.newPassword.maxLength),
            Validators.minLength(ValidationConstant.changePassword.newPassword.minLength),
          ],
        ],
        confirmPassword: [
          '',
          [
            Validators.pattern(ValidationConstant.passwordRegex),
            Validators.required,
            Validators.maxLength(ValidationConstant.changePassword.confirmPassword.maxLength),
            Validators.minLength(ValidationConstant.changePassword.confirmPassword.minLength),
          ],
        ],
      },
      formOptions
    );

    this.changePasswordForm.valueChanges.subscribe((data: any) => this.onValueChanged(data));

    this.loggedInUserData = this.storageService.get(StorageType.Session, 'userDetails')
      ? JSON.parse(this.storageService.get(StorageType.Session, 'userDetails') ?? '{}')
      : null;
  }
  onValueChanged(_data?: any) {
    if (!this.changePasswordForm) {
      return;
    }
    this.changePasswordformErrors = this.validator.validate(this.changePasswordForm);

    const confirmPasswordCtrl = this.changePasswordForm.controls['confirmPassword'];

    if (!confirmPasswordCtrl.touched) {
      return;
    }

    if (
      this.changePasswordForm.controls['confirmPassword'].value.length >
      ValidationConstant.changePassword.confirmPassword.maxLength
    ) {
      this.changePasswordformErrors.confirmPassword =
        MessageConstants.changePassword.confirmPasswordLengthError;
    } else if (confirmPasswordCtrl.errors?.['required']) {
      this.changePasswordformErrors.confirmPassword =
        MessageConstants.changePassword.confirmPasswordRequiredError;
      return;
    } else if (
      this.changePasswordForm.controls['newPassword'].value ===
      this.changePasswordForm.controls['confirmPassword'].value
    ) {
      this.changePasswordformErrors.confirmPassword = '';
    }
  }

  changePassword() {
    this.validator.validateAllFormFields(this.changePasswordForm);
    this.changePasswordformErrors = this.validator.validate(this.changePasswordForm);

    if (this.changePasswordForm.invalid) {
      return;
    }

    const oldPassword = this.changePasswordForm.controls['oldPassword'].value;
    const newPassword = this.changePasswordForm.controls['newPassword'].value;

    if (oldPassword === newPassword) {
      this.toasterService.showError(
        MessageConstants.common.errorNewPaaswordSameAsOldPassword,
        'full'
      );
      return;
    }

    const userData = {
      user: this.loginData.adminUser,
      userType: this.loginData.userType,
      parentId: this.loginData.parentId,
    };

    const reqObj = {
      oldPassword,
      newPassword,
    };

    this.isLoader.set(true);

    this.userChangePasswordService
      .changePassword(reqObj, userData.user, userData.userType, userData.parentId)
      .subscribe({
        next: () => {
          this.toasterService.showSuccess(MessageConstants.common.changePasswordMessage, 'full');
          this.router.navigate(['/login']);
        },
        error: (error: any) => {
          const toastMessage = Exception.exceptionMessage(error);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
        complete: () => {
          this.isLoader.set(false);
        },
      });
  }

  //make password visible
  onTogglePassword(fieldType: string) {
    if (fieldType === 'newPass') {
      this.newPassFieldType = this.newPassFieldType === 'password' ? 'text' : 'password';
    } else if (fieldType === 'currentPass') {
      this.currPassFieldType = this.currPassFieldType === 'password' ? 'text' : 'password';
    } else {
      this.confirmPassFieldType = this.confirmPassFieldType === 'password' ? 'text' : 'password';
    }
  }
}
